import { Trans, useTranslation } from 'react-i18next';
import { Text, CommonTextProps } from 'style-utils/components/Text';
import { Link, LinkProps } from 'react-router-dom';
import {
  TranslationLink,
  TranslationLinkProps,
} from 'style-utils/components/TranslationLink';
import { REACT_APP_MAIN_SITE_HOMEPAGE } from 'config';
import React from 'react';
/* -------------------------------------------------------------------------------------------------
 * DEFAULT TRANSLATION COMPONENTS
 * -----------------------------------------------------------------------------------------------*/
type HomePageLinkProps = React.ComponentPropsWithoutRef<'a'> &
  TranslationLinkProps;

const HomePageLink = ({ href, ...rest }: HomePageLinkProps) => (
  <TranslationLink
    href={`${REACT_APP_MAIN_SITE_HOMEPAGE}${href ?? ''}`}
    {...rest}
  />
);
/* -------------------------------------------------------------------------------------------------
 * BASE COMPONENT
 * -----------------------------------------------------------------------------------------------*/
type TypographyBaseProps = {
  i18nKey: string;
  components?: React.ReactElement[] | { [tagName: string]: React.ReactElement };
  values?: { [x: string]: any };
  theme?: null;
  as?: string | React.ComponentType<any>;
} & CommonTextProps;

type TypographyProps = Omit<TypographyBaseProps, 'as'>;

const TypographyBase = (props: TypographyBaseProps) => {
  const { i18nKey, values, components, m = '0', ...restOfProps } = props;
  const { t } = useTranslation();
  return (
    <Text m={m} {...restOfProps}>
      <Trans
        t={t}
        i18nKey={i18nKey}
        values={values}
        components={{
          ...components,
          a: <TranslationLink isUnderline />,
          HomePageLink: <HomePageLink isUnderline />,
        }}
      />
    </Text>
  );
};

/* -------------------------------------------------------------------------------------------------
 * SUB TYPOGRAPHIES
 * -----------------------------------------------------------------------------------------------*/
type AProps = TypographyProps & React.HTMLProps<HTMLAnchorElement> & LinkProps;
const A = (props: AProps) => <TypographyBase as={Link} {...props} />;

type H1Props = TypographyProps & React.HTMLProps<HTMLHeadingElement>;
const H1 = (props: H1Props) => <TypographyBase as="h1" {...props} />;

type H2Props = TypographyProps & React.HTMLProps<HTMLHeadingElement>;
const H2 = (props: H2Props) => <TypographyBase as="h2" {...props} />;

type H3Props = TypographyProps & React.HTMLProps<HTMLHeadingElement>;
const H3 = (props: H3Props) => <TypographyBase as="h3" {...props} />;

type H4Props = TypographyProps & React.HTMLProps<HTMLHeadingElement>;
const H4 = (props: H4Props) => <TypographyBase as="h4" {...props} />;

type H5Props = TypographyProps & React.HTMLProps<HTMLHeadingElement>;
const H5 = (props: H5Props) => <TypographyBase as="h5" {...props} />;

type LabelProps = TypographyProps & React.HTMLProps<HTMLLabelElement>;
const Label = (props: LabelProps) => <TypographyBase as="label" {...props} />;

type PProps = TypographyProps & React.HTMLProps<HTMLParagraphElement>;
const P = (props: PProps) => <TypographyBase as="p" {...props} />;

type SpanProps = TypographyProps & React.HTMLProps<HTMLSpanElement>;
const Span = (props: SpanProps) => <TypographyBase as="span" {...props} />;

export { HomePageLink, A, H1, H2, H3, H4, H5, Label, P, Span, TypographyBase };
export type {
  AProps,
  H1Props,
  H2Props,
  H3Props,
  H4Props,
  H5Props,
  LabelProps,
  PProps,
  SpanProps,
  TypographyBaseProps,
  TypographyProps,
};
